import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Stack,
  Portal,
  Center,
  VStack,
  HStack,
  Heading,
  CloseButton,
} from '@chakra-ui/react';
import { authService } from '../../services/api';
import AuthContext from '../../context/AuthContext';
import { useAppToast } from '../../utils/toast';

export function PasswordChangeModal({ isOpen, onClose }) {
  const { token } = useContext(AuthContext);
  const { showErrorToast, showSuccessToast } = useAppToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear the error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await authService.changePassword(formData.currentPassword, formData.newPassword);

      showSuccessToast('Password changed successfully');
      onClose();
      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
    } catch (error) {
      // The /change-password endpoint returns 401 only when the *current*
      // password is wrong — the request interceptor doesn't retry /auth/*
      // endpoints, so a 401 here is a credential error, not session expiry.
      if (error.response?.status === 401 || error.status === 401) {
        setErrors({ currentPassword: 'Invalid current password' });
      } else {
        showErrorToast(error.response?.data?.error || error.message || 'Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.600"
        zIndex={1000}
        onClick={handleClose}
      />
      <Center
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={1001}
        pointerEvents="none"
      >
        <Box
          bg="white"
          p={6}
          borderRadius="lg"
          boxShadow="xl"
          maxW="md"
          w="90%"
          pointerEvents="auto"
          onClick={(e) => e.stopPropagation()}
        >
          <HStack justify="space-between" mb={4}>
            <Heading size="md">Change Password</Heading>
            <CloseButton onClick={handleClose} />
          </HStack>
          
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              <FormControl isInvalid={errors.currentPassword}>
                <FormLabel>Current Password</FormLabel>
                <Input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.currentPassword && (
                  <FormErrorMessage>{errors.currentPassword}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={errors.newPassword}>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.newPassword && (
                  <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={errors.confirmPassword}>
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.confirmPassword && (
                  <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                )}
              </FormControl>
            </Stack>

            <HStack justify="flex-end" mt={6} spacing={3}>
              <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="brand"
                isLoading={isLoading}
                loadingText="Changing..."
              >
                Change Password
              </Button>
            </HStack>
          </form>
        </Box>
      </Center>
    </Portal>
  );
}